import { getAccessToken } from "./APIConnector";

/**
 * Fetches survey response database from server for export
 * 
 * @param {number} surveyID - Survey ID
 * @returns {Promise<Object>} Database dictionary { "id": response, ... }
 */
export async function fetchDatabase(surveyID) {
    const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    const token = await getAccessToken();
    if (!token) {
        const err = new Error("Not authenticated");
        err.status = 401;
        throw err;
    }

    const res = await fetch(`${baseUrl}/surveys/${surveyID}/database/`, {
        method: "GET",
        headers: {
            "Authorization": `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        let data = null;
        try {
            data = await res.json();
        } catch {
            data = null;
        }
        const message = (data && data.detail) || res.statusText || "Failed to fetch database";
        const err = new Error(message);
        err.status = res.status;
        err.data = data;
        throw err;
    }

    let data;
    try {
        data = await res.json();
    } catch {
        data = null;
    }

    return data;
}

/**
 * Export database to JSON file download
 * @param {Object} database - Database from fetchDatabase
 * @param {string} filename - Output filename
 */
export function exportDatabaseAsJSON(database, filename = 'survey-responses.json') {
    const blob = new Blob([JSON.stringify(database, null, 2)], {
        type: 'application/json;charset=utf-8'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

/**
 * Clears all survey responses from server for a specific survey
 * 
 * @param {string} surveyID - Survey ID
 * @param {string} confirmSurveyID - Survey ID confirmation (must match surveyID)
 * @returns {Promise<Object>} Response with deleted count
 */
export async function clearSurveyResponses(surveyID, confirmSurveyID) {
    const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    const token = await getAccessToken();
    if (!token) {
        const err = new Error("Not authenticated");
        err.status = 401;
        throw err;
    }

    const res = await fetch(`${baseUrl}/surveys/${surveyID}/clear-responses/`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            surveyId: confirmSurveyID
        }),
    });

    if (!res.ok) {
        let data = null;
        try {
            data = await res.json();
        } catch {
            data = null;
        }
        const message = (data && data.detail) || res.statusText || "Failed to clear survey responses";
        const err = new Error(message);
        err.status = res.status;
        err.data = data;
        throw err;
    }

    let data;
    try {
        data = await res.json();
    } catch {
        data = null;
    }

    return data;
}

/**
 * Deletes a survey and all associated data from server
 * 
 * @param {string} surveyID - Survey ID
 * @param {string} confirmSurveyID - Survey ID confirmation (must match surveyID)
 * @returns {Promise<Object>} Response with deleted survey info
 */
export async function deleteSurvey(surveyID, confirmSurveyID) {
    const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    const token = await getAccessToken();
    if (!token) {
        const err = new Error("Not authenticated");
        err.status = 401;
        throw err;
    }

    const res = await fetch(`${baseUrl}/surveys/${surveyID}/delete/`, {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            surveyId: confirmSurveyID
        }),
    });

    if (!res.ok) {
        let data = null;
        try {
            data = await res.json();
        } catch {
            data = null;
        }
        const message = (data && data.detail) || res.statusText || "Failed to delete survey";
        const err = new Error(message);
        err.status = res.status;
        err.data = data;
        throw err;
    }

    let data;
    try {
        data = await res.json();
    } catch {
        data = null;
    }

    return data;
}
